import Dropzone, {
    formatBytes,
    formatDuration,
    IFileWithMeta,
    IInputProps,
    ILayoutProps,
    IPreviewProps,
    MethodValue,
    StatusValue,
} from 'react-dropzone-uploader'
import { ReactNode, useEffect } from 'react'
import { ModalDataAttributes } from 'components/Modals/types'
import Cookies from 'js-cookie'
import getConfig from 'next/config'
import { useAppContext } from 'context/AppContext'
import Translation from 'components/Translation'
import { useState } from 'react'
import { useAuth } from '../../context/AuthContext'
import { getTranslation } from 'utils/language-helper'
import { GlobalDialogType } from '../Modals/GlobalDialog'
import { ImageIcon } from 'utils/custom-icons'

type FileUploader = {
    uploadStatus?(status: string): void
    tenant_id: string | undefined
    staff_modal?: boolean
    accept: string
    addClassNames: Record<string, string>
    form_context?: {
        attributes: ModalDataAttributes
        setAttributes(data: Record<string, string | File>): void
    }
    onAdd?(image_url: string): void
    onRemove?(): void
}

export function ImageFileUploaderLayout(props: ILayoutProps) {
    const {
        input,
        previews,
        submitButton,
        dropzoneProps,
        files,
        extra: { maxFiles },
    } = props

    return (
        <div {...dropzoneProps}>
            <div className="relative w-full h-full">
                {previews}

                {files.length < maxFiles && input}

                {files.length > 0 && submitButton}
            </div>
        </div>
    )
}

export function ImageFileUploaderPreview(
    props: {
        RemoveButton?: ReactNode
    } & IPreviewProps
) {
    const {
        className,
        style,
        meta: {
            name = '',
            percent = 0,
            size = 0,
            previewUrl,
            status,
            duration,
            validationError,
        },
        imageClassName,
        imageStyle,
        fileWithMeta: { cancel, remove, restart },
        isUpload,
        canCancel,
        canRemove,
        canRestart,
        extra: { minSizeBytes },
    } = props

    let title = `${name || '?'}, ${formatBytes(size)}`
    if (duration) title = `${title}, ${formatDuration(duration)}`
    if (status === 'error_file_size' || status === 'error_validation') {
        return (
            <div className={className} style={style}>
                <span className="dzu-previewFileNameError">{title}</span>
                {status === 'error_file_size' && (
                    <span>
                        {size < minSizeBytes
                            ? 'File too small'
                            : 'File too big'}
                    </span>
                )}
                {status === 'error_validation' && (
                    <span>{String(validationError)}</span>
                )}
                {canRemove && !props.RemoveButton && (
                    <span
                        className="dzu-previewButton feather-trash"
                        onClick={remove}
                    />
                )}
            </div>
        )
    }

    return (
        <div className={className} style={style}>
            {previewUrl ? (
                <div
                    className="w-full h-64 rounded"
                    style={{
                        ...imageStyle,
                        background: `url(${previewUrl}) no-repeat center`,
                        backgroundSize: 'cover',
                    }}
                    title={title}
                />
            ) : (
                ''
            )}

            <div className="dzu-previewStatusContainer flex flex-col justify-start absolute w-full h-48">
                {isUpload && (
                    <progress
                        max={100}
                        value={
                            status === 'done' ||
                            status === 'headers_received'
                                ? 100
                                : percent
                        }
                    />
                )}

                {status === 'uploading' && canCancel && (
                    <span
                        className="dzu-previewButton feather-x-circle"
                        onClick={cancel}
                    />
                )}
                {status !== 'preparing' &&
                    status !== 'getting_upload_params' &&
                    status !== 'uploading' &&
                    canRemove && (
                        <span
                            className="feather-x rounded-full h-9 w-9 px-2 py-1 text-center text-base cursor-pointer absolute right-1 top-1 bg-black text-white border-2 border-white font-bold"
                            onClick={remove}
                        />
                    )}
                {[
                    'error_upload_params',
                    'exception_upload',
                    'error_upload',
                    'aborted',
                    'ready',
                ].includes(status) &&
                    canRestart && (
                        <span
                            className="dzu-previewButton feather-refresh-cw"
                            onClick={restart}
                        />
                    )}
            </div>
        </div>
    )
}

export function FileInputComponent(props: IInputProps) {
    const {
        className,
        labelClassName,
        labelWithFilesClassName,
        style,
        labelStyle,
        labelWithFilesStyle,
        getFilesFromEvent,
        accept,
        multiple,
        disabled,
        content,
        withFilesContent,
        onFiles,
        files,
    } = props

    const { GlobalDialog } = useAuth()

    const { translations } = useAppContext()

    return (
        <label
            className={
                files.length > 0 ? labelWithFilesClassName : labelClassName
            }
            style={files.length > 0 ? labelWithFilesStyle : labelStyle}
        >
            {files.length > 0 ? (
                withFilesContent
            ) : (
                <div className="text-center text-base w-full h-64">
                    <div className="border-dashed border-2 rounded-lg p-3 h-48 flex flex-col gap-2 items-center justify-center">
                        <ImageIcon
                            style={{ width: '40px', height: '40px' }}
                        />
                        <Translation
                            render_as="p"
                            className="text-center text-primary"
                            content_key="drag_and_drop"
                        />
                        <p className="leading-none text-center text-gray-300">
                            <small>
                                <Translation content_key="upload_dimension_limit" />
                            </small>
                            <br />
                            <small>
                                <Translation content_key="upload_supported_extension" />
                            </small>
                        </p>
                    </div>
                    <div className="block h-3" />

                    <div className="flex gap-3 items-center bg-primary-light text-white px-6 py-3 rounded-lg w-full text-lg justify-center">
                        <ImageIcon
                            color="white"
                            style={{ width: '24px', height: '24px' }}
                        />
                        <Translation
                            render_as="span"
                            className="leading-snug"
                            content_key="upload_photo"
                        />
                    </div>
                </div>
            )}
            <input
                className={className}
                style={style}
                type="file"
                accept={accept}
                multiple={multiple}
                disabled={disabled}
                onChange={async (e) => {
                    const target = e.target
                    const chosenFiles = await getFilesFromEvent(e)
                    onFiles(chosenFiles)
                    //@ts-ignore
                    target.value = null
                    const file_name = chosenFiles[0].name
                    var idxDot = file_name.lastIndexOf('.') + 1
                    var extFile = file_name
                        .substr(idxDot, file_name.length)
                        .toLowerCase()
                    if (!accept.includes(extFile)) {
                        GlobalDialog.setAttributes({
                            message: `${getTranslation(
                                'error_file_type',
                                translations
                            )}`,
                            className: 'w-1/3 overflow-hidden',
                            messageClassName:
                                'bg-red-200/50 text-black w-full',
                            type: GlobalDialogType.ALERT,
                        })
                        GlobalDialog.open()
                    }
                }}
            />
        </label>
    )
}

export function StaffFileInputComponent(props: IInputProps) {
    const {
        className,
        labelClassName,
        labelWithFilesClassName,
        style,
        labelStyle,
        labelWithFilesStyle,
        getFilesFromEvent,
        accept,
        multiple,
        disabled,
        content,
        withFilesContent,
        onFiles,
        files,
    } = props

    const { GlobalDialog } = useAuth()

    const { translations } = useAppContext()

    return (
        <label>
            {files.length > 0 ? (
                withFilesContent
            ) : (
                <div className="grid grid-flow-col">
                    <div className="col">
                        <div className="bg-gray-200 items-center w-24 h-24 text-center justify-center p-1 flex v-center text-gray-400 text-md font-bold rounded-full">
                            <ImageIcon
                                style={{ width: '24px', height: '24px' }}
                            />
                        </div>
                    </div>
                    <div className="col-span-4">
                        <Translation
                            render_as="span"
                            className="text-lg"
                            content_key="staff_profile_photo"
                        />
                        <div className="flex gap-3">
                            <div className="flex gap-3 items-center bg-primary-light text-white px-2 py-1 rounded-lg text-sm justify-center cursor-pointer">
                                <ImageIcon style={{ color: 'white' }} />
                                <Translation
                                    render_as="span"
                                    className="text-md"
                                    content_key="upload_photo"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <input
                className={className}
                style={style}
                type="file"
                accept={accept}
                multiple={multiple}
                disabled={disabled}
                onChange={async (e) => {
                    const target = e.target
                    const chosenFiles = await getFilesFromEvent(e)
                    onFiles(chosenFiles)
                    //@ts-ignore
                    target.value = null
                    const file_name = chosenFiles[0].name
                    var idxDot = file_name.lastIndexOf('.') + 1
                    var extFile = file_name
                        .substr(idxDot, file_name.length)
                        .toLowerCase()
                    if (!accept.includes(extFile)) {
                        GlobalDialog.setAttributes({
                            message: `${getTranslation(
                                'error_file_type',
                                translations
                            )}`,
                            className: 'w-1/3 overflow-hidden',
                            messageClassName:
                                'bg-red-200/50 text-black w-full',
                            type: GlobalDialogType.ALERT,
                        })
                        GlobalDialog.open()
                    }
                }}
            />
        </label>
    )
}

export function ImageFileUploader(props: FileUploader) {
    const { NEXT_PUBLIC_AWS_S3_BUCKET_HOST } =
        getConfig().publicRuntimeConfig
    const { translations } = useAppContext()
    const [upload_status, setUploadStatus] = useState<string>('')

    useEffect(() => {
        if (props.uploadStatus) {
            props.uploadStatus(upload_status)
        }
    }, [upload_status])

    function handleSubmit(
        clean_files: IFileWithMeta[],
        all_files: IFileWithMeta[]
    ) {
        console.log(clean_files, all_files)
    }

    function handleFileAdd(
        { file }: IFileWithMeta,
        status: StatusValue,
        files: IFileWithMeta[]
    ) {
        setUploadStatus(status)
        if (status != 'done') {
            return
        }
        const image_url = `https://${NEXT_PUBLIC_AWS_S3_BUCKET_HOST}/${props.tenant_id}/${file.name}`
        if (props.form_context) {
            setUploadStatus(status)
            props.form_context.setAttributes({
                ...props.form_context.attributes,
                file,
                image_url: image_url,
            })
        }
        if (props.onAdd) {
            props.onAdd(image_url)
        }
    }
    return (
        <Dropzone
            {...props}
            multiple={false}
            maxFiles={1}
            minSizeBytes={1000}
            maxSizeBytes={4000000}
            onChangeStatus={handleFileAdd}
            getUploadParams={() => {
                const headers = {
                    Authorization: `Bearer ${Cookies.get('id_token')}`,
                }
                const method = 'PUT' as MethodValue
                const { API_ENDPOINT } = getConfig().publicRuntimeConfig
                return {
                    url: `${API_ENDPOINT}/v1/aws-integrations/s3?tenantId=${props.tenant_id}`,
                    method: method,
                    headers: headers,
                }
            }}
            submitButtonContent={
                <>
                    <ImageIcon />
                    <Translation
                        render_as="span"
                        content_key="upload_photo"
                    />
                </>
            }
            InputComponent={
                props.staff_modal
                    ? StaffFileInputComponent
                    : FileInputComponent
            }
            LayoutComponent={(l) => <ImageFileUploaderLayout {...l} />}
            PreviewComponent={(p) => (
                <ImageFileUploaderPreview
                    {...p}
                    className="border-dashed border-2 rounded-lg p-3 h-48 flex flex-col gap-2 items-center justify-center "
                />
            )}
        />
    )
}

export default ImageFileUploader
